const SENSITIVE_KEY = /(?:email|e-mail|phone|mobile|telephone|name|surname|first.?name|last.?name|contact|message|comment|qualification|experience|course|level|treatment|practice.?years|business|clinic|salon|company|organisation|organization|team|staff|employee|postcode|postal|address|consent|token|secret|password|answer|response|free.?text|notes?|^cv$|cover.?letter|portfolio.?url|notice.?period)/i;
const PRIVATE_HEADER = /^(?:authorization|cookie|set-cookie|x-api-key)$/i;
const JOURNEY_PATH = /^\/(?:which-aesthetics-course-is-right-for-me|best-aesthetic-course-for-you|courses\/(?:beginner|career-change|beauty-therapists|medical-professionals|rpl)|business-training|resources\/clinic-team-and-treatment-pathway-planner)(?:\/|$)/i;
const SENSITIVE_API_PATH = /^\/api\/(?:[^/]*(?:callback|enquir|lead|contact|pathway|team|business|quiz)[^/]*)(?:\/|$)/i;
const CAREERS_PATH = /^\/(?:careers(?:\/|$)|api\/careers\/apply(?:\/|$))/i;
const CHECKOUT_PATH = /^\/(?:checkout(?:\/|$)|enrolment(?:\/|$)|api\/(?:checkout|enroll|complete|sellout-offer)(?:\/|$))/i;
const REDACTED = "[Filtered]";
const SAFE_QUERY_KEYS = new Set([
  "persona",
  "source",
  "type",
  "placement",
  "utm_source",
  "utm_medium",
  "utm_campaign",
]);
const SAFE_QUERY_VALUE = /^[a-zA-Z0-9][a-zA-Z0-9._-]{0,63}$/;

function pathnameOf(value) {
  if (typeof value !== "string") return "";
  const requestLinePath = value.match(/^(?:GET|POST|PUT|PATCH|DELETE|OPTIONS)\s+(\S+)/i)?.[1];
  if (requestLinePath) return pathnameOf(requestLinePath);
  try {
    return new URL(value, "https://sanitizer.invalid").pathname;
  } catch {
    return value.split(/[?#]/, 1)[0];
  }
}

function isSensitivePath(value) {
  const pathname = pathnameOf(value);
  return JOURNEY_PATH.test(pathname) || SENSITIVE_API_PATH.test(pathname) || CAREERS_PATH.test(pathname);
}

/** Retain the useful route and safe query context while dropping sensitive values. */
export function sanitizeSentryUrl(value) {
  if (typeof value !== "string") return value;
  try {
    const parsed = new URL(value, "https://sanitizer.invalid");
    for (const key of [...parsed.searchParams.keys()]) {
      if (isSensitivePath(parsed.pathname)) {
        const values = parsed.searchParams.getAll(key);
        parsed.searchParams.delete(key);
        if (
          SAFE_QUERY_KEYS.has(key.toLowerCase()) &&
          values.length === 1 &&
          SAFE_QUERY_VALUE.test(values[0])
        ) {
          parsed.searchParams.set(key, values[0]);
        }
      } else if (SENSITIVE_KEY.test(key)) {
        parsed.searchParams.set(key, REDACTED);
      }
    }
    // Fragments can be used to carry form values; keep only simple anchor ids.
    if (parsed.hash && !/^#[a-z0-9_-]{1,80}$/i.test(parsed.hash)) parsed.hash = "";
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return value.split(/[?#]/, 1)[0];
  }
}

function sanitizeObject(value, { stripBody = false } = {}) {
  if (Array.isArray(value)) return value.map((item) => sanitizeObject(item, { stripBody }));
  if (!value || typeof value !== "object") return value;

  const result = {};
  for (const [key, item] of Object.entries(value)) {
    if (PRIVATE_HEADER.test(key)) continue;
    if (SENSITIVE_KEY.test(key)) {
      result[key] = REDACTED;
    } else if (stripBody && /^(?:data|body|payload|requestBody|responseBody)$/i.test(key)) {
      result[key] = REDACTED;
    } else {
      result[key] = sanitizeObject(item, { stripBody });
    }
  }
  return result;
}

function sanitizeQueryString(value, sensitivePath = false) {
  const query = new URLSearchParams(value.replace(/^\?/, ""));
  for (const key of [...query.keys()]) {
    if (sensitivePath) {
      const values = query.getAll(key);
      query.delete(key);
      if (
        SAFE_QUERY_KEYS.has(key.toLowerCase()) &&
        values.length === 1 &&
        SAFE_QUERY_VALUE.test(values[0])
      ) {
        query.set(key, values[0]);
      }
    } else if (SENSITIVE_KEY.test(key)) {
      query.set(key, REDACTED);
    }
  }
  return query.toString();
}

function sanitizeQueryObject(value, sensitivePath) {
  if (!sensitivePath) return sanitizeObject(value);
  const result = {};
  for (const [key, item] of Object.entries(value)) {
    if (
      SAFE_QUERY_KEYS.has(key.toLowerCase()) &&
      typeof item === "string" &&
      SAFE_QUERY_VALUE.test(item)
    ) {
      result[key] = item;
    }
  }
  return result;
}

/** Remove identifying or submitted content from a Sentry event request context. */
export function sanitizeSentryEvent(event) {
  if (!event || typeof event !== "object") return event;

  const requestUrl = event.request?.url || event.transaction || "";
  const sensitivePath = isSensitivePath(requestUrl);
  const checkoutPath = CHECKOUT_PATH.test(pathnameOf(requestUrl));
  if (typeof event.transaction === "string") {
    event.transaction = sanitizeSentryUrl(event.transaction);
  }
  if (event.request) {
    event.request = { ...event.request };
    if (typeof event.request.url === "string") {
      event.request.url = sanitizeSentryUrl(event.request.url);
    }
    if (event.request.query_string && typeof event.request.query_string === "object") {
      event.request.query_string = sanitizeQueryObject(event.request.query_string, sensitivePath);
    } else if (typeof event.request.query_string === "string") {
      event.request.query_string = sanitizeQueryString(event.request.query_string, sensitivePath);
    }
    if (event.request.headers) event.request.headers = sanitizeObject(event.request.headers);
    if (sensitivePath || checkoutPath) delete event.request.cookies;
    if (event.request.data !== undefined) {
      event.request.data = sensitivePath || checkoutPath
        ? REDACTED
        : sanitizeObject(event.request.data);
    }
  }

  if (checkoutPath) {
    delete event.user;
    if (event.contexts) event.contexts = sanitizeObject(event.contexts, { stripBody: true });
    if (event.extra) event.extra = sanitizeObject(event.extra, { stripBody: true });
  }

  if (sensitivePath) {
    delete event.user;
    if (event.extra) event.extra = sanitizeObject(event.extra, { stripBody: true });
    if (event.contexts) event.contexts = sanitizeObject(event.contexts, { stripBody: true });
    if (Array.isArray(event.exception?.values)) {
      event.exception.values = event.exception.values.map(({ type }) => ({
        ...(type ? { type } : {}),
        value: "Exception on enquiry journey",
      }));
    }
    if (typeof event.message === "string") event.message = "Exception on enquiry journey";
  }
  if (event.extra && !sensitivePath) event.extra = sanitizeObject(event.extra);

  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb) =>
      sanitizeSentryBreadcrumb(breadcrumb, sensitivePath ? requestUrl : ""),
    );
  }
  return event;
}

/** Preserve method/status/route diagnostics while removing request payload data. */
export function sanitizeSentryBreadcrumb(breadcrumb, eventUrl = "") {
  if (!breadcrumb || typeof breadcrumb !== "object") return breadcrumb;
  const result = { ...breadcrumb };
  const url = result.data?.url || result.data?.request?.url || eventUrl;
  const sensitivePath = isSensitivePath(url) || CHECKOUT_PATH.test(pathnameOf(url));
  if (sensitivePath && typeof result.message === "string") {
    result.message = "Request on enquiry journey";
  }
  if (!result.data || typeof result.data !== "object") return result;
  if (sensitivePath && !result.data.url && !result.data.request?.url) {
    delete result.data;
    return result;
  }
  result.data = sanitizeObject(result.data, { stripBody: sensitivePath });
  if (typeof result.data.url === "string") {
    result.data.url = sanitizeSentryUrl(result.data.url);
  }
  if (result.data.request && typeof result.data.request === "object") {
    if (typeof result.data.request.url === "string") {
      result.data.request.url = sanitizeSentryUrl(result.data.request.url);
    }
    if (sensitivePath && result.data.request.data !== undefined) {
      result.data.request.data = REDACTED;
    }
  }
  if (sensitivePath && typeof result.data.query_string === "string") {
    result.data.query_string = sanitizeQueryString(result.data.query_string, true);
  }
  return result;
}

export const __private = { isSensitivePath, SENSITIVE_KEY };
